import React, { useMemo } from "react";
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  ApolloProvider,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { useAuthToken, SESSION_TIMED_OUT } from "./useAuthToken";
import { useLocation } from "react-router-dom";
import { isHomeEDApp, apiUrl, isDev } from "@constants";
import { logger } from "../utils/logger";
import {
  captureGraphQLError,
  captureNetworkError,
} from "../utils/sentryErrorCapture";

if (!apiUrl) {
  throw new Error("Data URL not specified.");
}

const httpLink = new HttpLink({ uri: apiUrl });

const cache = new InMemoryCache({
  typePolicies: {
    GroupContent: {
      keyFields: ["contentId", "groupId"],
    },
    AllContent: {
      fields: {
        groups: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    School: {
      fields: {
        courses: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Unit: {
      fields: {
        teacherPreparation: {
          merge(_, incoming) {
            return incoming;
          },
        },
        classPresentation: {
          merge(_, incoming) {
            return incoming;
          },
        },
        studyGuides: {
          merge(_, incoming) {
            return incoming;
          },
        },
        studyGuideMaterials: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Lesson: {
      fields: {
        modules: {
          merge(_, incoming) {
            return incoming;
          },
        },
        materials: {
          merge(_, incoming) {
            return incoming;
          },
        },
        sections: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

const authMiddleware = (authToken: string | null) =>
  new ApolloLink((operation, forward) => {
    if (authToken) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${authToken}`,
        },
      });
    }

    return forward(operation);
  });

const errorLink = (logout: () => void, user?: any) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        // TODO: Remove commenting later - temporarily logging all errors in production for debugging
        // if (isDev) {
        logger.warn(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          null,
          {
            component: "Apollo",
            action: "GraphQLError",
            additionalData: { timestamp: new Date().toISOString() },
          },
        );
        // }

        // Check for session timeout flag before handling auth errors
        const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
        if (sessionTimedOut) {
          logger.warn("[Apollo] Ignoring GraphQL error due to timeout", null, {
            component: "Apollo",
            action: "IgnoreTimeoutError",
            additionalData: {
              errorMessage: message,
              timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
              timestamp: new Date().toISOString(),
            },
          });
          return;
        }

        // Handle auth errors, but exclude certain operations that might return "Not Authorised!" for permission reasons
        const fieldName = path?.[0] ? String(path[0]) : "";
        const declaredOperationName = operation?.operationName
          ? String(operation.operationName)
          : "";
        const operationName = fieldName || declaredOperationName;

        // List of operations that should not trigger logout on "Not Authorised!" errors
        // These are typically permission-based errors rather than authentication errors
        const permissionBasedOperations = [
          "generateAWSKeys",
          "customers",
          "me",
          "userTimeSpent", // Skip logout for time tracking errors
          "heaMe",
          // Groups — used by lesson exclusions UI (ContentAdmin)
          "listGroups",
          // Media uploads used in super-admin lesson/unit edit flows
          "GetUrl",
          "videoUpload",
          "getZIPSlideUrl",
          "uploadZIPSlide",
          // Non-SLL section mutations used in lesson edit sidebar
          "deleteSection",
          "updateSection",
          // SLL — ContentAdmin may get "Not Authorised!" from backend; show error, don't logout
          "sllGetLessons",
          "sllGetGettingStartedLessons",
          "sllGetCaregiverLessons",
          "sllGetLessonsForSA",
          "sllGetLessonInfo",
          "sllGetLessonInfoForSA",
          "sllGetLessonInfoByUniqueName",
          "sllGetLessonExclusions",
          "sllGetCourses",
          "sllGetGroupModules",
          "sllGetBrainBreakLessons",
          "sllGetLastVisitedModule",
          "isValidSLLUniqueName",
          "sllCreateLesson",
          "sllUpdateLesson",
          "sllDeleteLesson",
          "sllAddLessonExclusion",
          "sllRemoveLessonExclusion",
          "sllCreateSection",
          "sllUpdateSection",
          "sllDeleteSection",
          "sllCreateModule",
          "sllUpdateModule",
          "sllDeleteModule",
          "sllUpdateVideoModule",
          "sllMoveModule",
          "sllCreateGroupModule",
          "sllUpdateGroupModule",
          "sllDeleteGroupModule",
          "sllDeleteModuleContent",
          "sllDeleteGroupModuleContent",
          "sllUpdateVideoGroupModule",
          "sllUpdateCourse",
          "sllCreateCourse",
          "sllUpdateCourseName",
          "sllDeleteCourse",
          "sllUpdateLessonCompleteStatus",
          "sllUpdateLastVisitedModule",
        ];

        const isPermissionError =
          message === "Not Authorised!" &&
          permissionBasedOperations.some(
            (name) => name === fieldName || name === declaredOperationName,
          );

        if (
          message === "Failed to verify the supplied jwt header." ||
          (message === "Not Authorised!" && !isPermissionError)
        ) {
          logger.warn("[Apollo] Logging out due to auth error", null, {
            component: "Apollo",
            action: "AuthError",
            additionalData: {
              errorMessage: message,
              operationName,
              timestamp: new Date().toISOString(),
            },
          });
          logout?.();
        } else if (isPermissionError) {
          logger.warn("[Apollo] Permission error (not logging out)", null, {
            component: "Apollo",
            action: "PermissionError",
            additionalData: {
              errorMessage: message,
              operationName,
              timestamp: new Date().toISOString(),
            },
          });
          // Don't log out for permission errors on specific operations
        }

        // Filter out noisy GraphQL errors before sending to Sentry
        const noisyGraphQLErrors = [
          "Error while converting text to audio",
          "This survey already expired",
          "SSO_AUTH_FAILED",
          "convertTextToAudio",
          "Most active tab check",
          "Force check running (leader tab)",
          "Force check running",
          "Temporarily disabled to prevent re-render loop",
        ];

        const shouldSkipSentryCapture = noisyGraphQLErrors.some((pattern) =>
          message.toLowerCase().includes(pattern.toLowerCase()),
        );

        // Capture GraphQL errors to Sentry (but not auth/permission errors or noisy errors)
        if (
          !sessionTimedOut &&
          message !== "Failed to verify the supplied jwt header." &&
          !isPermissionError &&
          !shouldSkipSentryCapture
        ) {
          try {
            // Use centralized GraphQL error capture with user ID
            captureGraphQLError(
              new Error(`GraphQL Error: ${message}`),
              {
                operationName: operation?.operationName,
                variables: operation?.variables,
                query: operation?.query?.loc?.source?.body,
                message,
                locations: locations ? [...locations] : undefined, // Convert readonly array
                path: path ? [...path] : undefined, // Convert readonly array
                extensions,
              },
              user?.id,
            );
          } catch (sentryError) {
            logger.error(
              "[Apollo] Error capturing GraphQL error to Sentry:",
              sentryError,
            );
          }
        }
      });
    }

    if (networkError) {
      // Log network errors with console.warn to ensure they're preserved in production
      logger.warn(`[Apollo] Network error:`, networkError, {
        component: "Apollo",
        action: "NetworkError",
        additionalData: {
          timestamp: new Date().toISOString(),
        },
      });

      // Capture network errors to Sentry
      const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
      if (!sessionTimedOut) {
        try {
          // Use centralized network error capture with user ID
          captureNetworkError(
            networkError,
            {
              operationName: operation?.operationName,
              statusCode: (networkError as any)?.statusCode,
            },
            user?.id,
          );
        } catch (sentryError) {
          logger.error(
            "[Apollo] Error capturing network error to Sentry:",
            sentryError,
          );
        }
      }
    }
  });

// createPersistedQueryLink({
//   sha256,
//   useGETForHashedQueries: true,
// }).concat(new ApolloLink((operation, forward) => {
//   if (authToken) {
//     operation.setContext({
//       headers: {
//         authorization: `Bearer ${authToken}`,
//       },
//     });
//   }

//   return forward(operation);
// }));

export const useAppApolloClient = () => {
  logger.info("[Apollo] useAppApolloClient rendering");
  const { hsaToken, token, logout } = useAuthToken();

  return useMemo(() => {
    logger.info("[Apollo] Creating new Apollo client");
    return new ApolloClient({
      link: from([
        errorLink(logout),
        authMiddleware(isHomeEDApp ? hsaToken : token).concat(httpLink),
      ]),
      cache,
    });
  }, [hsaToken, token, logout]);
};

export const useParentAppApolloClient = () => {
  const { parentToken, logout } = useAuthToken();

  return useMemo(() => {
    return new ApolloClient({
      link: from([
        errorLink(logout),
        authMiddleware(parentToken).concat(httpLink),
      ]),
      cache,
    });
  }, [parentToken, logout]);
};

export const useHomeSchoolApolloClient = () => {
  const { hsaToken, logout } = useAuthToken();

  return useMemo(() => {
    return new ApolloClient({
      link: from([
        errorLink(logout),
        authMiddleware(hsaToken).concat(httpLink),
      ]),
      cache,
    });
  }, [hsaToken, logout]);
};

export const AppApolloProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const client = useAppApolloClient();
  const parentClient = useParentAppApolloClient();
  const hsaClient = useHomeSchoolApolloClient();
  const { pathname } = useLocation();

  // Memoize the client selection to prevent unnecessary re-renders
  const selectedClient = useMemo(() => {
    if (pathname.startsWith("/parent")) return parentClient;
    if (pathname.startsWith("/homeschool")) return hsaClient;
    return client;
  }, [pathname, parentClient, hsaClient, client]);

  return <ApolloProvider client={selectedClient}>{children}</ApolloProvider>;
};
